import React, { useState } from 'react';
import { Spin, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  LoadingOutlined, StarOutlined, StarFilled, RollbackOutlined, ShareAltOutlined,
  PaperClipOutlined, SafetyCertificateOutlined,
  FileOutlined, FileImageOutlined, FilePdfOutlined, FileWordOutlined,
  FileExcelOutlined, FileZipOutlined, UpOutlined, DownOutlined, CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import axiosInstance from '../../variables/api/axiosInstance';
import T from './theme';
import { NATURES_COURRIER } from './categorieMessage';
import OssanEditor from './OssanEditor';
import { formatAdresses } from './adressesMessage';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';
import { libellesTraduits } from '../../i18n/libelles';

const G = T.green;
const PAL = ['#5b7c99','#8e6c88','#a8744f','#6b8e6b','#9c6b6b','#5f8a8a','#7a6b9c','#998b5b','#5b8e7a','#9c7a6b'];

const DELAI_LABEL = libellesTraduits('messagerie:lecteur.delais');
const DELAI_COLOR = { critical: { bg: '#fee2e2', fg: '#991b1b', dot: '#dc2626' }, urgent: { bg: '#fff7ed', fg: '#9a3412', dot: '#ea580c' }, attention: { bg: '#fefce8', fg: '#854d0e', dot: '#ca8a04' } };

function avatarColor(s) {
  let h = 0;
  for (const c of String(s || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return PAL[h % PAL.length];
}

function ini(n) {
  const p = String(n || '').trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || p[0]?.[1] || '')).toUpperCase();
}

function fDateLong(d) {
  if (!d) return '';
  return new Date(d).toLocaleString(localeIntl(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/* ───── Empty state ───── */
function EmptyState() {
  const { t } = useTranslation('messagerie');
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 60, textAlign: 'center', background: '#fff',
    }}>
      <svg width="96" height="96" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: .25, marginBottom: 20 }}>
        <rect x="10" y="18" width="76" height="60" rx="6" stroke={G} strokeWidth="3" fill="none" />
        <path d="M10 30l38 26 38-26" stroke={G} strokeWidth="3" strokeLinejoin="round" fill="none" />
        <circle cx="68" cy="68" r="18" fill="#f0fdf4" stroke={G} strokeWidth="2.5" />
        <path d="M61 68h14M68 61v14" stroke={G} strokeWidth="2.5" strokeLinecap="round" />
      </svg>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#6b7280' }}>{t('lecteur.vide')}</div>
      <div style={{ fontSize: 13, marginTop: 8, color: '#9ca3af', maxWidth: 280, lineHeight: 1.5 }}>
        {t('lecteur.videAide')}
      </div>
    </div>
  );
}

/* ───── Avatar ───── */
function Av({ name, size = 36 }) {
  const col = avatarColor(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: col,
      color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 600, fontSize: size * .33, flexShrink: 0,
    }}>
      {ini(name)}
    </div>
  );
}

/* ───── Bandeau délai CEDEAO ───── */
function DelaiCEDEAO({ conv, onRelance }) {
  const { t } = useTranslation('messagerie');
  if (!conv.criticality) return null;
  const c = DELAI_COLOR[conv.criticality];
  return (
    <div style={{
      background: c.bg, color: c.fg, padding: '10px 20px',
      display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
      borderBottom: `1px solid ${c.dot}30`,
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.dot, flexShrink: 0, display: 'inline-block', animation: conv.criticality === 'critical' ? 'cbpulse 1.4s infinite' : 'none' }} />
      <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>
        {DELAI_LABEL[conv.criticality]}
        {conv.critDays != null && ` - ${conv.criticality === 'critical' ? t('lecteur.joursRetard', { n: conv.critDays }) : t('lecteur.joursRestants', { n: conv.critDays })}`}
        {conv.sinistre && ` · ${t('lecteur.dossier', { numero: conv.sinistre })}`}
      </span>
      <button
        onClick={onRelance}
        style={{
          background: c.dot, color: '#fff', border: 'none', borderRadius: 6,
          padding: '5px 12px', cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
          fontFamily: 'inherit',
        }}
      >
        {t('lecteur.creerRelance')}
      </button>
    </div>
  );
}

/* ───── Un message dans le thread ───── */
function MessageItem({ msg, idx, isLast, onReply, onForward, pushToast }) {
  const { t } = useTranslation('messagerie');
  const [open, setOpen] = useState(isLast);
  const [dlHov, setDlHov] = useState(null);

  const from = msg.from || {};
  // « to » peut arriver en chaîne selon la source (aperçu IMAP) : on ne suppose rien.
  const toArr = Array.isArray(msg.to)
    ? msg.to
    : (msg.to ? [{ name: String(msg.to), email: String(msg.to) }] : []);
  // Même prudence que pour « to » : la copie peut arriver en chaîne ou absente.
  const ccArr = Array.isArray(msg.cc)
    ? msg.cc
    : (msg.cc ? [{ name: String(msg.cc), email: String(msg.cc) }] : []);

  const downloadAttachment = async (att) => {
    // Garde explicite : sans identifiant IMAP, l'URL contiendrait « null » et
    // l'appel echouerait avec un message opaque. On le dit plutot que de l'envoyer.
    if (!att?.messageIdImap) {
      console.error('[messagerie] piece jointe sans messageIdImap', att);
      pushToast(<WarningOutlined />, t('lecteur.pieceSansIdentifiant'));
      return;
    }
    const url = `/v1/messagerie/pieces-jointes/${encodeURIComponent(att.messageIdImap)}/${att.id}`;
    try {
      const r = await axiosInstance.get(url, { responseType: 'blob' });
      // Chemin nominal instrumenté : un 200 avec un corps JSON d'erreur (ou un
      // blob vide) passait jusqu'ici sans laisser de trace exploitable.
      console.log('[messagerie] telechargement piece jointe', { url, size: r.data?.size, type: r.data?.type });
      if (r.data?.type === 'application/json') {
        console.error('[messagerie] reponse JSON inattendue pour une piece jointe', { url, size: r.data?.size, type: r.data?.type });
        pushToast(<WarningOutlined />, t('lecteur.reponseInattendue'));
        return;
      }
      const objectUrl = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = att.nom || att.filename || 'fichier';
      a.click();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      // Le catch nu d'origine rendait toute panne muette. On journalise l'URL
      // reellement appelee, le code HTTP et le corps de la reponse.
      let corps = e?.response?.data;
      if (corps instanceof Blob) {
        // responseType 'blob' fait que meme une reponse d'erreur arrive en Blob :
        // sans cette lecture, la console n'afficherait qu'un « Blob {size, type} ».
        try { corps = await corps.text(); } catch { corps = '(corps illisible)'; }
      }
      console.error('[messagerie] echec telechargement piece jointe', {
        url,
        status: e?.response?.status,
        data: corps,
        att,
      });
      pushToast(<WarningOutlined />, t('lecteur.telechargementImpossible', { cause: e?.response?.status || t('lecteur.reseau') }));
    }
  };

  return (
    <div style={{
      border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 10,
      overflow: 'hidden', background: '#fff',
      boxShadow: open ? '0 2px 8px rgba(0,0,0,.06)' : 'none',
    }}>
      {/* En-tête du message (toujours visible) */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
          cursor: 'pointer', userSelect: 'none',
          background: open ? '#fafafa' : '#fff',
        }}
      >
        <Av name={from.name || from.email || ''} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: '#111827' }}>{from.name || from.email}</div>
          {!open && (
            <div style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {msg.preview || msg.corps?.slice(0, 120) || ''}
            </div>
          )}
          {open && (
            <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <span style={{ color: '#9ca3af', minWidth: 28, flexShrink: 0 }}>{t('lecteur.de')}</span>
                <span
                  title={from.email || from.name || ''}
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {formatAdresses([from])}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <span style={{ color: '#9ca3af', minWidth: 28, flexShrink: 0 }}>{t('lecteur.a')}</span>
                <span
                  title={toArr.map(t => t.email || t.name).join(', ')}
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {formatAdresses(toArr)}
                </span>
              </div>
              {ccArr.length > 0 && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <span style={{ color: '#9ca3af', minWidth: 28, flexShrink: 0 }}>{t('lecteur.cc')}</span>
                  <span
                    title={ccArr.map(c => c.email || c.name).join(', ')}
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {formatAdresses(ccArr)}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{ fontSize: 12, color: '#9ca3af', flexShrink: 0 }}>{fDateLong(msg.date)}</div>
        <span style={{ color: '#9ca3af', fontSize: 12 }}>{open ? <UpOutlined /> : <DownOutlined />}</span>
      </div>

      {/* Corps du message */}
      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          {/* Corps HTML ou texte */}
          <div className="cb-msg-body" style={{ paddingTop: 16 }}>
            {msg.corpsHtml
              ? <OssanEditor readOnly value={msg.corpsHtml} />
              : <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, fontSize: 14, lineHeight: 1.6, color: T.text }}>{msg.corps}</pre>}
          </div>

          {/* Pièces jointes */}
          {msg.piecesJointes?.length > 0 && (
            <div style={{ marginTop: 20, borderTop: '1px solid #f0f0f0', paddingTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <PaperClipOutlined /> {t('lecteur.piecesJointes', { count: msg.piecesJointes.length })}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {msg.piecesJointes.map((att, ai) => (
                  <button
                    key={ai}
                    onClick={() => downloadAttachment(att)}
                    onMouseEnter={() => setDlHov(ai)}
                    onMouseLeave={() => setDlHov(null)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      border: '1px solid #e5e7eb', background: dlHov === ai ? '#f0fdf4' : '#f9fafb',
                      borderRadius: 7, padding: '7px 12px', cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: 12.5, color: '#374151',
                      transition: 'background .1s',
                    }}
                  >
                    <span>{fileIcon(att.nom || att.filename || '')}</span>
                    <span>{att.nom || att.filename || t('lecteur.fichier')}</span>
                    {att.taille && <span style={{ color: '#9ca3af' }}>({fSize(att.taille)})</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions répondre / transférer */}
          <div style={{ marginTop: 18, display: 'flex', gap: 8 }}>
            <MsgActionBtn icon={<RollbackOutlined />} label={t('lecteur.repondre')} onClick={() => onReply(msg)} />
            <MsgActionBtn icon={<ShareAltOutlined />} label={t('lecteur.transferer')} onClick={() => onForward(msg)} />
          </div>
        </div>
      )}
    </div>
  );
}

function MsgActionBtn({ icon, label, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 5,
        border: '1px solid #e5e7eb', background: hov ? '#f0fdf4' : '#fff',
        borderRadius: 7, padding: '6px 14px', cursor: 'pointer',
        fontFamily: 'inherit', fontSize: 13, color: '#374151',
        transition: 'background .1s',
      }}
    >
      <span>{icon}</span> {label}
    </button>
  );
}

function fileIcon(name) {
  const ext = name.split('.').pop().toLowerCase();
  if (['pdf'].includes(ext)) return <FilePdfOutlined style={{ color: '#dc2626' }} />;
  if (['jpg','jpeg','png','gif','webp'].includes(ext)) return <FileImageOutlined style={{ color: '#0e7490' }} />;
  if (['doc','docx'].includes(ext)) return <FileWordOutlined style={{ color: '#2563eb' }} />;
  if (['xls','xlsx'].includes(ext)) return <FileExcelOutlined style={{ color: '#15803d' }} />;
  if (['zip','rar','7z'].includes(ext)) return <FileZipOutlined style={{ color: '#a16207' }} />;
  return <FileOutlined style={{ color: '#64748b' }} />;
}

function fSize(bytes) {
  if (bytes < 1024) return i18n.t('messagerie:lecteur.tailles.octets', { n: bytes });
  if (bytes < 1048576) return i18n.t('messagerie:lecteur.tailles.ko', { n: (bytes/1024).toFixed(0) });
  return i18n.t('messagerie:lecteur.tailles.mo', { n: (bytes/1048576).toFixed(1) });
}

/* ───── Barre réponse rapide ───── */
function QuickReply({ conv, onSend, pushToast }) {
  const { t } = useTranslation('messagerie');
  const [expanded, setExpanded] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  async function send() {
    if (!body.trim()) return;
    setSending(true);
    try {
      const destinataire = conv.participants[0]?.email || '';
      await axiosInstance.post('/v1/messagerie/envoyer', {
        destinataire,
        sujet: `Re: ${conv.sujet || ''}`,
        corpsHtml: body,
        nature: 'COURRIER_SORTANT',
        sinistreTrackingId: conv.raw?.sinistreTrackingId || null,
      });
      pushToast(<CheckCircleOutlined />, t('lecteur.messageEnvoye'));
      setBody(''); setExpanded(false);
    } catch {
      pushToast(<WarningOutlined />, t('lecteur.echecEnvoi'));
    } finally { setSending(false); }
  }

  return (
    <div style={{
      borderTop: '1px solid #e5e7eb', padding: '14px 20px',
      background: '#fff', flexShrink: 0,
    }}>
      {!expanded ? (
        <div
          onClick={() => setExpanded(true)}
          style={{
            border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 14px',
            cursor: 'text', color: '#9ca3af', fontSize: 13.5,
            background: '#fafafa',
          }}
        >
          {t('lecteur.repondreA', { nom: conv.participants[0]?.name || conv.participants[0]?.email || '' })}
        </div>
      ) : (
        <div style={{ border: `1px solid ${G}`, borderRadius: 8, overflow: 'hidden' }}>
          <OssanEditor
            value={body}
            onChange={setBody}
            placeholder={t('lecteur.repondreAPlaceholder', { nom: conv.participants[0]?.name || '' })}
            minHeight={120}
            autoFocus
          />
          <div style={{
            borderTop: '1px solid #f0f0f0', padding: '8px 12px',
            display: 'flex', alignItems: 'center', gap: 8, background: '#fafafa',
          }}>
            <IconToolBtn title={t('lecteur.pieceJointe')} icon={<PaperClipOutlined />} />
            <div style={{ flex: 1 }} />
            <button
              onClick={() => { setExpanded(false); setBody(''); }}
              style={{
                border: 'none', background: 'transparent', cursor: 'pointer',
                color: '#6b7280', fontSize: 12.5, fontFamily: 'inherit', padding: '5px 8px',
              }}
            >
              {t('lecteur.annuler')}
            </button>
            <button
              onClick={send} disabled={!body.trim() || sending}
              style={{
                background: T.accent, color: '#fff', border: 'none', borderRadius: 7,
                padding: '7px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                fontFamily: 'inherit', opacity: !body.trim() || sending ? .5 : 1,
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {sending && <Spin indicator={<LoadingOutlined style={{ fontSize: 13, color: '#fff' }} spin />} />}
              {t('lecteur.envoyer')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function IconToolBtn({ icon, title, style: s }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      title={title}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        border: 'none', background: hov ? '#e5e7eb' : 'transparent',
        width: 28, height: 28, borderRadius: 4, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13.5, color: '#374151', ...s,
      }}
    >
      {icon}
    </button>
  );
}

/* ═══════════════════════════════════════ COMPOSANT PRINCIPAL ═══════════════════════════════════════ */
export default function LecteurConversation({ conv, loading, onReply, onForward, onStar, onClasser, openCompose, onCreateRelance, pushToast, user, folder, expanded, onToggle, quickExpanded, onQuickToggle, onQuickSend }) {
  const { t } = useTranslation('messagerie');
  const _pushToast = pushToast || (() => {});

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
        <div style={{ textAlign: 'center' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: G }} spin />} />
          <div style={{ marginTop: 12, fontSize: 13, color: '#9ca3af' }}>{t('lecteur.chargement')}</div>
        </div>
      </div>
    );
  }

  if (!conv) return <EmptyState />;

  const messages = conv.messages || [conv];
  const hasMulti  = messages.length > 1;

  function handleRelance() {
    if (onCreateRelance) { onCreateRelance(); return; }
    if (openCompose) openCompose({
      to: conv.participants,
      sinistre: conv.sinistre,
      nature: 'RELANCE',
      sujet: `RELANCE – ${conv.sujet}`,
    });
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff', fontFamily: T.font }}>

      {/* ── En-tête conversation ── */}
      <div style={{ padding: '16px 24px 12px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        {/* Titre + actions */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', lineHeight: 1.3, marginBottom: 8 }}>
              {conv.sujet}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {conv.sinistre && (
                <span style={{
                  padding: '3px 10px', borderRadius: 5, background: T.badge.sinistre.bg, color: T.badge.sinistre.fg,
                  fontSize: 12, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {conv.sinistre}
                </span>
              )}
              {/* Catégorie modifiable : c'est elle qui range le message dans les
                  dossiers thématiques, et un classement automatique se trompe
                  parfois - sans correction possible, l'agent subirait l'erreur. */}
              <Select
                size="small"
                variant="borderless"
                value={conv.nature && conv.nature !== 'AUTRE' ? conv.nature : undefined}
                placeholder={t('lecteur.classer')}
                options={NATURES_COURRIER}
                onChange={(v) => onClasser?.(v)}
                disabled={!onClasser}
                style={{ minWidth: 168 }}
                popupMatchSelectWidth={false}
                aria-label={t('lecteur.categorie')}
                className="cb-nature-select"
              />
              {conv.criticality === 'critical' && (
                <span style={{ padding: '3px 10px', borderRadius: 5, background: T.badge.critical.bg, color: T.badge.critical.fg, fontSize: 12, fontWeight: 600, animation: 'cbpulse 1.4s infinite' }}>
                  {t('lecteur.critique')}
                </span>
              )}
              {hasMulti && (
                <span style={{ padding: '3px 10px', borderRadius: 5, background: '#f3f4f6', color: '#6b7280', fontSize: 12 }}>
                  {t('lecteur.nbMessages', { n: messages.length })}
                </span>
              )}
            </div>
          </div>

          {/* Boutons d'en-tête */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <span style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6,
              background: T.badge.nature.bg, color: T.badge.nature.fg, fontSize: 11.5, fontWeight: 600,
              whiteSpace: 'nowrap',
            }}>
              <SafetyCertificateOutlined /> {t('lecteur.apercuSecurise')}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <HeaderBtn icon={conv.starred ? <StarFilled style={{ color: '#f59e0b' }} /> : <StarOutlined />} title={conv.starred ? t('lecteur.retirerSuivis') : t('lecteur.ajouterSuivis')} onClick={() => onStar(conv)} />
              <HeaderBtn icon={<RollbackOutlined />} title={t('lecteur.repondre')} onClick={() => onReply(conv)} />
              <HeaderBtn icon={<ShareAltOutlined />} title={t('lecteur.transferer')} onClick={() => onForward(conv)} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Bandeau délai CEDEAO ── */}
      <DelaiCEDEAO conv={conv} onRelance={handleRelance} />

      {/* ── Thread de messages ── */}
      <div className="cb-scroll" style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
        {messages.map((msg, idx) => (
          <MessageItem
            key={msg.id || idx}
            msg={msg}
            idx={idx}
            isLast={idx === messages.length - 1}
            onReply={onReply}
            onForward={onForward}
            pushToast={_pushToast}
          />
        ))}
      </div>

      {/* ── Réponse rapide ── */}
      <QuickReply conv={conv} onSend={onReply} pushToast={_pushToast} />
    </div>
  );
}

function HeaderBtn({ icon, title, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick} title={title}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 34, height: 34, border: '1px solid #e5e7eb',
        background: hov ? '#f0fdf4' : '#fff', borderRadius: 7,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', fontSize: 14, color: '#374151',
        transition: 'background .1s',
      }}
    >
      {icon}
    </button>
  );
}
